import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import {
  Activity, Calendar, Thermometer, TestTube, Heart, Sparkles, Loader2, Lightbulb,
} from "lucide-react";
import PremiumToolsSection from "@/components/PremiumToolsSection";
import { API, useAuth } from "@/App";

const StatTile = ({ icon: Icon, label, value, hint, testId }) => (
  <Card className="glass border-white/10" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-rose-200" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-mystic-300">{label}</p>
          <p className="text-xl font-semibold text-white mt-0.5">{value}</p>
          {hint && <p className="text-xs text-mystic-300/80 mt-0.5">{hint}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const PHASE_LABEL = {
  menstrual: "Menstrual",
  follicular: "Follicular",
  ovulation: "Ovulation",
  luteal: "Luteal",
};

const PHASE_GRADIENT = {
  menstrual: "from-rose-500/30 to-rose-700/10 border-rose-400/30",
  follicular: "from-emerald-500/25 to-teal-700/10 border-emerald-400/30",
  ovulation: "from-amber-400/30 to-amber-600/10 border-amber-400/40",
  luteal: "from-purple-500/25 to-violet-700/10 border-purple-400/30",
};

export default function TTCDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/wellness/ttc/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-300 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-mystic-200">Switch to TTC mode in Settings to see this dashboard.</p>
      </div>
    );
  }

  const { ttc_journey, current_phase, cycle_day, recent_tracking, daily_tip } = data;
  const monthsLabel =
    ttc_journey?.duration_months != null ? `${ttc_journey.duration_months} months` : "—";

  return (
    <div className="space-y-8" data-testid="ttc-dashboard-page">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-400/20 text-rose-100 text-xs uppercase tracking-widest mb-3">
          <Activity className="w-3.5 h-3.5" /> TTC Dashboard
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif text-white">Your fertility journey</h1>
        <p className="text-mystic-200 text-sm mt-2">
          A focused snapshot of where you are in your cycle and what you've been tracking.
        </p>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      >
        <Card
          className={`bg-gradient-to-br ${PHASE_GRADIENT[current_phase] || "from-mystic-800 to-mystic-900 border-white/10"}`}
          data-testid="ttc-phase-hero"
        >
          <CardContent className="p-6 sm:p-8 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70">Current phase</p>
              <h2 className="text-3xl sm:text-4xl font-serif text-white mt-1">
                {PHASE_LABEL[current_phase] || current_phase}
              </h2>
              <p className="text-white/80 text-sm mt-2">Day {cycle_day} of your cycle</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-white/60">Journey</p>
              <p className="text-2xl font-semibold text-white mt-1" data-testid="ttc-journey-duration">
                {monthsLabel}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatTile
          icon={Thermometer}
          label="BBT entries"
          value={recent_tracking.bbt_entries}
          hint={recent_tracking.last_bbt ? `Last: ${recent_tracking.last_bbt.date}` : "Last 14 days"}
          testId="ttc-stat-bbt"
        />
        <StatTile
          icon={TestTube}
          label="OPK entries"
          value={recent_tracking.opk_entries}
          hint={
            recent_tracking.recent_positive_opk
              ? `Surge: ${recent_tracking.recent_positive_opk.date}`
              : "Last 14 days"
          }
          testId="ttc-stat-opk"
        />
        <StatTile
          icon={Heart}
          label="Intimacy"
          value={recent_tracking.intimacy_entries}
          hint="Last 14 days"
          testId="ttc-stat-intimacy"
        />
      </div>

      {daily_tip && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <Card className="glass border-amber-400/20" data-testid="ttc-daily-tip-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-amber-200 mb-1">
                    Tip of the day {daily_tip.category ? `· ${daily_tip.category}` : ""}
                  </p>
                  <h3 className="text-lg font-semibold text-white">{daily_tip.title}</h3>
                  <p className="text-mystic-200 text-sm mt-2 leading-relaxed">{daily_tip.body}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <PremiumToolsSection
        tools={[
          {
            icon: Lightbulb,
            label: "Conception Tips Library",
            description: "Curated, evidence-based fertility guidance",
            to: "/dashboard",
            accent: "bg-rose-500/20 text-rose-300",
            testId: "premium-tool-conception-tips",
          },
        ]}
      />

      <p className="text-xs text-mystic-300/70 flex items-center gap-2">
        <Calendar className="w-3 h-3" /> Tracking shown reflects last 14 days.
      </p>
    </div>
  );
}
